import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';
import type { AxiosResponse } from 'axios';
import type { VypisyudajuSouboryUlozkaDto } from '@gov-nx/api';
import {
	extractOfDataGetFileIdQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { GovError } from '@gov-nx/core/app';
import { useDownloadEvents } from '@gov-nx/core/events';
import type { RegistrZPSubject } from '@gov-nx/core/hooks';
import {
	RegistrZPOperace,
	useProcessControl,
	useRegistrZPLoader,
	useSaveToDocuments,
} from '@gov-nx/core/hooks';
import { compose, propEq } from '@gov-nx/core/types';
import type { ServiceCode } from '../definitions/codes';
import {
	getAuthorizationServiceName,
	useNeverejnyVypisZZRAuthorization,
} from './authorization';
import { FormInstance, prepareSubmitData } from './FormDefinitions';
import type {
	FormData,
	ServiceContextControls,
	ServiceContextTypes,
} from './service.types';
import { getFileIdFromResponseData } from './utils';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';

export const ServiceContext = createContext<ServiceContextTypes | null>(null);

export interface ServiceContextProviderProps {
	children: React.ReactNode;
	code: ServiceCode;
}

export function NeverejnyVypisZZRContextProvider({
	children,
	code,
}: ServiceContextProviderProps) {
	const { downloadFile } = useDownloadEvents();
	const { axiosInstance } = useAxiosInstance();
	const { saveToDocuments } = useSaveToDocuments({
		onSuccess: () => {
			setControls({ processLoading: false });
			formDefinition.formReset();
		},
		onError: (error) =>
			setControls({ processError: error, processLoading: false }),
	});
	const { controls, setControls } = useProcessControl<ServiceContextControls>({
		dataError: null,
		downloadLoading: false,
		downloadError: null,
	});

	const [subjects, setSubjects] = useState<RegistrZPSubject[]>([]);

	const { load: registrZPLoad } = useRegistrZPLoader({
		onError: (error) =>
			setControls({ initialLoading: false, initialError: error }),
		onDataError: (error) =>
			setControls({ initialLoading: false, dataError: error }),
		onSuccess: (subjects) => {
			setControls({ initialLoading: false });
			setSubjects(subjects);
		},
	});

	useEffect(() => {
		setControls({ initialLoading: true });
		registrZPLoad().finally();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, []);

	const authContext = useAutorizaceContext();
	const authorizationForBranch = useNeverejnyVypisZZRAuthorization();

	const formDefinition = FormInstance({
		code,
		isConsentRequired: (vztahOsoby) =>
			authorizationForBranch(vztahOsoby).isLowAuthorization,
	});

	// Derive the UI flags from the *selected* branch through the same
	// getAuthorizationServiceName mapping onInitAuthorization uses below, so the
	// consent checkbox and "Autorizovat a …" prefix always reflect the code that
	// will actually be authorized — never the other branch's config.
	const vztahOsoby = formDefinition.formMethods.watch('_vztahOsoby');
	const { isLowAuthorization, isAuthorizationEnforced } =
		authorizationForBranch(vztahOsoby);

	const submitMutation = usePoMutation<
		AxiosResponse<VypisyudajuSouboryUlozkaDto>,
		FormData
	>({
		mutationFn: compose(
			extractOfDataGetFileIdQuery(axiosInstance),
			prepareSubmitData
		),
		onError: (error) =>
			setControls({ processError: error, processLoading: false }),
		onSuccess: async (response, variables) => {
			const fileId = getFileIdFromResponseData(response.data);
			if (fileId) {
				saveToDocuments.mutate({ id: fileId, axiosInstance });
				const nonHighAuthorizationFlow = () => undefined;
				if (authContext) {
					authContext.onInitAuthorization(
						getAuthorizationServiceName(variables._vztahOsoby),
						response.headers['x-autorizaceid'],
						nonHighAuthorizationFlow
					);
				}
			} else {
				setControls({
					processError: new GovError('Error: no document id in response.'),
					processLoading: false,
				});
			}
		},
	});

	const downloadMutation = usePoMutation<
		AxiosResponse<VypisyudajuSouboryUlozkaDto>,
		FormData
	>({
		mutationFn: compose(
			extractOfDataGetFileIdQuery(axiosInstance),
			prepareSubmitData
		),
		onError: (error) =>
			setControls({ downloadError: error, downloadLoading: false }),
		onSuccess: async (response, variables) => {
			const fileId = getFileIdFromResponseData(response.data);
			if (fileId) {
				downloadFile({
					id: fileId,
					callback: () => {
						setControls({ downloadLoading: false });
						formDefinition.formReset();
					},
				});
				const nonHighAuthorizationFlow = () => undefined;
				if (authContext) {
					authContext.onInitAuthorization(
						getAuthorizationServiceName(variables._vztahOsoby),
						response.headers['x-autorizaceid'],
						nonHighAuthorizationFlow
					);
				}
			} else {
				setControls({
					processError: new GovError('Error: no document id in response.'),
					processLoading: false,
				});
			}
		},
	});

	const handleSubmit = useCallback(async () => {
		setControls({ processError: null, processLoading: true });
		submitMutation.mutate({
			...formDefinition.formMethods.getValues(),
			operace: RegistrZPOperace.ULOZIT_DO_DOKUMENTU,
		});
	}, [formDefinition.formMethods, setControls, submitMutation]);

	const handleDownload = useCallback(async () => {
		setControls({ downloadError: null, downloadLoading: true });
		downloadMutation.mutate({
			...formDefinition.formMethods.getValues(),
			operace: RegistrZPOperace.STAHNOUT,
		});
	}, [downloadMutation, formDefinition.formMethods, setControls]);

	const onSubmit = formDefinition.formMethods.handleSubmit(handleSubmit);
	const onDownload = formDefinition.formMethods.handleSubmit(handleDownload);

	return (
		<ServiceContext.Provider
			value={{
				code,
				formDefinition,
				onSubmit,
				onDownload,
				controls,
				setControls,
				subjects: subjects.filter(propEq('hasSomeActiveRole', true)),
				isLowAuthorization,
				isAuthorizationEnforced,
			}}>
			{children}
		</ServiceContext.Provider>
	);
}

export const useNeverejnyVypisZZRContext = (): ServiceContextTypes =>
	useContext(ServiceContext) as ServiceContextTypes;
